import { useState } from 'react';

import type { EmoteMap } from '../../api/types.js';
import { segmentEmotes } from '../../chat/emotes.js';
import { nameColor } from '../../chat/name-colors.js';
import '../widget.css';
import { platformLabel } from '../../platforms/labels.js';

/**
 * The widget renderer (ARCHITECTURE.md §103.6).
 *
 * ==================== ONE RENDERER, THREE CALLERS ====================
 * The browser source, the gallery card and the editor preview all draw
 * through this file. That is deliberate and it is the whole point of the
 * preview-first design: a preview built from a separate mock would be a
 * drawing of a widget rather than the widget, and the two would drift the
 * first time either changed. What an operator sees while configuring is the
 * component OBS will run.
 *
 * It is PURE. No fetching, no token, no polling, no body class — those belong
 * to the browser source, which is the only caller that has a token at all.
 * Give it a type, a configuration and a state, and it draws.
 * =====================================================================
 *
 * ==================== UNTRUSTED CONTENT ====================
 * Display names, chat bodies and provider-formatted amounts are all written
 * by whoever is in the channel. Every one is a React text child and never
 * `dangerouslySetInnerHTML`. There is no configuration field in Slice 1 that
 * accepts markup, CSS or script (§103.9), so nothing here is ever asked to
 * interpret one.
 * ===========================================================
 */

type FeedItem = {
  platform: string;
  eventType: string;
  displayName: string | null;
  avatarUrl: string | null;
  amount: number | null;
  currency: string | null;
  displayAmount: string | null;
  occurredAt: string;
};

type ChatLine = {
  id: string;
  platform: string;
  displayName: string | null;
  text: string | null;
  /**
   * Normalized badges. Present for replayed messages too — the worker writes
   * them into `chat_messages.metadata` (§103.13).
   */
  badges?: { id: string; label?: string; version?: string }[];
  /**
   * Live messages only.
   *
   * `chat_messages` has no avatar column, so a REPLAYED message never carries
   * one and falls back to an initial. Not a bug to fix later: the column does
   * not exist, and inventing one to back-fill a stream overlay would be
   * storing a provider CDN URL that expires.
   */
  avatarUrl?: string | null;
  occurredAt: string;
};

/**
 * Every colour a widget may use, resolved once.
 *
 * The schema (`WIDGET_COLORS`) bounds the NAME; this bounds the value. The
 * renderer therefore never interpolates an operator-supplied string into a
 * style, which is what keeps a configuration field from becoming a CSS
 * injection vector on a public page.
 */
const COLOR_VALUES: Record<string, string> = {
  white: '#ffffff',
  amber: '#ffc55c',
  green: '#7fe08a',
  cyan: '#6fd8e8',
  violet: '#b39dff',
  pink: '#ff9ecb',
  red: '#ff8a8a',
};

/**
 * A stable colour for one person.
 *
 * `nameColor` is the SAME function the Console chat uses — twelve hues, each
 * contrast-checked by its own test, chosen by FNV-1a over the lower-cased
 * name. One implementation, so a regular viewer is the same colour in the
 * operator's Console and on the broadcast; two would drift and the drift
 * would be visible to the one person who notices these things, the streamer.
 *
 * One caveat worth stating: that palette's contrast floor was measured
 * against Connaro's own dark surface, and an overlay sits over video nobody
 * can predict. That is what `textOutline` and `textShadow` are for, and why
 * the Minimal Transparent preset turns one of them on.
 */
function autoColorFor(name: string): string {
  return nameColor(name, true) ?? '#ffffff';
}

/** Clock time, in the viewer's own locale. Off by default on an overlay (§31). */
function clockTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * One normalized badge.
 *
 * Rendered as its LABEL, as text — never as provider HTML and never as a
 * remote image. Connaro serves Twitch badge images to the Console from its own
 * origin, but an overlay resolving them would need the badge set for the
 * channel, which the normalized message does not carry. A short text chip is
 * the honest version: it says "MOD" because the provider said moderator.
 */
function BadgeChips({ badges }: { badges: ChatLine['badges'] }): React.JSX.Element | null {
  if (badges === undefined || badges.length === 0) return null;

  return (
    <>
      {badges.slice(0, 3).map((badge, index) => (
        <span key={`${badge.id}-${String(index)}`} className="widget__badge">
          {(badge.label ?? badge.id).slice(0, 12)}
        </span>
      ))}
    </>
  );
}


function styleOf(config: Record<string, unknown> | null): {
  vars: React.CSSProperties;
  className: string;
} {
  const style = (config?.style ?? {}) as Record<string, unknown>;
  const align = typeof style.align === 'string' ? style.align : 'left';
  const scale = typeof style.fontScale === 'number' ? style.fontScale : 1;

  return {
    vars: {
      // Every value came through the server's schema, which bounds all of
      // them. Nothing here is a string the operator typed.
      '--widget-scale': scale,
      '--widget-bg-opacity': typeof style.backgroundOpacity === 'number' ? style.backgroundOpacity : 0,
      '--widget-radius': typeof style.cornerRadius === 'number' ? style.cornerRadius : 8,
      '--widget-gap': typeof style.spacing === 'number' ? style.spacing : 8,
    } as React.CSSProperties,
    className: [
      'widget',
      /*
       * THE THEME IS NOW DRAWN.
       *
       * `theme` has been in the schema since Slice 1, stored on every widget,
       * and ignored by this function — a field that was a feature everywhere
       * except the code that produces pixels. It survived review twice. It is
       * a class like every other bounded style value; the enum is closed, so
       * this cannot interpolate anything an operator typed.
       */
      `widget--theme-${typeof style.theme === 'string' ? style.theme : 'minimal'}`,
      `widget--font-${typeof style.font === 'string' ? style.font : 'system'}`,
      `widget--anim-${typeof style.animation === 'string' ? style.animation : 'fade'}`,
      `widget--${align === 'center' || align === 'right' ? align : 'left'}`,
      style.textShadow === false ? '' : 'widget--shadow',
      style.textOutline === true ? 'widget--outline' : '',
      style.animate === false ? 'widget--static' : '',
    ]
      .filter(Boolean)
      .join(' '),
  };
}

/**
 * An avatar that cannot break the widget.
 *
 * A provider CDN goes down, a URL expires, an account is deleted — none of
 * which should leave a hole on a broadcast. The image is replaced by the
 * first character of the name the moment it fails to load (§103.6).
 */
function Avatar({ name, url }: { name: string; url: string | null }): React.JSX.Element {
  const [failed, setFailed] = useState(false);
  const initial = (name.trim()[0] ?? '?').toUpperCase();

  if (url === null || url === '' || failed) {
    return (
      <span className="widget__initial" aria-hidden="true">
        {initial}
      </span>
    );
  }

  return (
    <img
      className="widget__avatar"
      src={url}
      alt=""
      onError={() => {
        setFailed(true);
      }}
    />
  );
}

const EVENT_VERBS: Record<string, string> = {
  follow: 'followed',
  subscription: 'subscribed',
  membership: 'became a member',
  gift: 'gifted a subscription',
  donation: 'donated',
  raid: 'raided',
};

function FeedRow({ item }: { item: FeedItem }): React.JSX.Element {
  const name = item.displayName ?? 'Someone';
  const amount = item.displayAmount ?? (item.amount === null ? null : String(item.amount));

  return (
    <li className="widget__item">
      <Avatar name={name} url={item.avatarUrl} />
      <span>
        <span className="widget__name">{name}</span> {EVENT_VERBS[item.eventType] ?? item.eventType}
        {amount === null ? null : <span className="widget__amount">{amount}</span>}
      </span>
    </li>
  );
}

/**
 * The chat overlay.
 *
 * ==================== UNTRUSTED, TWICE OVER ====================
 * A display name AND a message body, both written by whoever is in the
 * channel. Every one is a React text child: a viewer who types markup appears
 * on stream as characters. There is no emote rendering here yet precisely
 * because that is the feature that would need to interpret something.
 * ===============================================================
 */
/**
 * A message body, with third-party emotes.
 *
 * `segmentEmotes` is the SAME function the Console chat uses — whole-word,
 * case-sensitive matching, because `catJAM` and `catjam` are different emotes
 * and a substring match would turn "Sadge" inside "Sadgerly" into a picture.
 * One implementation, so an overlay and the Console never disagree about what
 * a message said.
 *
 * Images go through Connaro's own proxy, same-origin, which is what lets them
 * render under a CSP of `img-src 'self'`. The widget token rides along because
 * a browser source has no session; see the note on the proxy route.
 */
function MessageText({
  text,
  emotes,
  token,
}: {
  text: string;
  emotes: EmoteMap;
  token: string | null;
}): React.JSX.Element {
  const suffix = token === null ? '' : `?k=${encodeURIComponent(token)}`;

  return (
    <>
      {segmentEmotes(text, emotes).map((piece, index) =>
        piece.kind === 'emote' ? (
          <img
            key={`${piece.name}-${String(index)}`}
            className="widget__emote"
            src={`/api/v1/emotes/${piece.provider}/${piece.id}/1x${suffix}`}
            alt={piece.name}
            title={piece.name}
          />
        ) : (
          // Still a text child. An emote NAME that is not an emote stays text.
          <span key={`t-${String(index)}`}>{piece.text}</span>
        ),
      )}
    </>
  );
}

function ChatBody({
  config,
  messages,
  emotes,
  token,
}: {
  config: Record<string, unknown> | null;
  messages: readonly ChatLine[];
  emotes: EmoteMap;
  token: string | null;
}): React.JSX.Element {
  const max = typeof config?.maxMessages === 'number' ? config.maxMessages : 15;
  const kept = messages.slice(-max);

  /*
   * Reversing the ARRAY rather than the flex direction.
   *
   * `column-reverse` would put the newest line at the top visually while
   * leaving it last in the DOM, so a screen reader and a copy-paste would both
   * read the overlay backwards. Reversing the data keeps the two in agreement.
   */
  const shown = config?.newestAt === 'top' ? [...kept].reverse() : kept;

  /*
   * ATTRIBUTION IS DECIDED HERE, NOT CONFIGURED.
   *
   * §23's review permits merged display only where a viewer cannot be confused
   * about a message's origin, so the moment a second platform is present every
   * line carries its mark. There is no config field to switch it off, and this
   * reads the platform COUNT rather than any stored preference — so an overlay
   * that becomes merged becomes attributed in the same instant.
   *
   * A single-platform overlay omits it, because there is nothing to confuse it
   * with and a repeated identical mark is noise on a broadcast.
   */
  const platforms = new Set(shown.map((line) => line.platform));
  const attribute = platforms.size > 1;

  const showAvatar = config?.showAvatar === true;
  const showBadges = config?.showBadges !== false;
  const showTimestamp = config?.showTimestamp === true;

  /*
   * Read as a string or not at all.
   *
   * The config arrives as `unknown` from a jsonb column, so coercing with
   * `String()` would turn an object into "[object Object]" and then look it up
   * — a miss that silently falls back to white. A type check makes the
   * fallback happen for the right reason.
   */
  const colorOf = (value: unknown): string =>
    (typeof value === 'string' ? COLOR_VALUES[value] : undefined) ?? '#ffffff';

  const fixedName =
    config?.usernameColor === 'fixed' ? colorOf(config.usernameColorValue) : null;
  const messageColor = colorOf(config?.messageColor);

  return (
    <ul className="widget__feed">
      {shown.map((line) => {
        const name = line.displayName ?? 'Someone';

        return (
          <li key={line.id} className="widget__item">
            {attribute && (
              <img
                className="widget__mark"
                src={`/platforms/${line.platform}.svg`}
                alt={line.platform}
                onError={(event) => {
                  // A missing mark must not leave a message unattributed: fall
                  // back to the platform's name as text.
                  event.currentTarget.replaceWith(
                    Object.assign(document.createElement('span'), {
                      className: 'widget__mark-text',
                      textContent: platformLabel(line.platform),
                    }),
                  );
                }}
              />
            )}

            {showAvatar && <Avatar name={name} url={line.avatarUrl ?? null} />}

            <span style={{ color: messageColor }}>
              {showTimestamp && (
                <span className="widget__time">{clockTime(line.occurredAt)}</span>
              )}
              {showBadges && <BadgeChips badges={line.badges} />}
              <span
                className="widget__name"
                style={{ color: fixedName ?? autoColorFor(name) }}
              >
                {name}
              </span>{' '}
              <MessageText text={line.text ?? ''} emotes={emotes} token={token} />
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function GoalBody({ state }: { state: Record<string, unknown> }): React.JSX.Element {
  const current = typeof state.currentValue === 'number' ? state.currentValue : 0;
  const target = typeof state.target === 'number' ? state.target : 0;
  const fraction = target > 0 ? Math.max(0, Math.min(1, current / target)) : 0;

  return (
    <div>
      <div className="widget__goal-values">
        {current.toLocaleString()} / {target.toLocaleString()}
      </div>
      {/*
        * A progress bar that is meaningful to somebody who cannot see it. The
        * numbers above are the same fact in text, so colour alone carries
        * nothing (§65).
        */}
      <div
        className="widget__goal-track"
        role="progressbar"
        aria-valuenow={current}
        aria-valuemin={0}
        aria-valuemax={target}
      >
        <div className="widget__goal-fill" style={{ width: `${String(fraction * 100)}%` }} />
      </div>
    </div>
  );
}

function EventLabel({
  config,
  latest,
}: {
  config: Record<string, unknown> | null;
  latest: FeedItem | null | undefined;
}): React.JSX.Element {
  const prefix = typeof config?.prefix === 'string' ? config.prefix : '';
  const suffix = typeof config?.suffix === 'string' ? config.suffix : '';
  const fallback = typeof config?.fallbackText === 'string' ? config.fallbackText : '—';

  const name = latest?.displayName ?? null;

  return (
    <span className="widget__label">
      {prefix}
      {name ?? fallback}
      {suffix}
    </span>
  );
}

/**
 * Draw a widget.
 *
 * `state` is whatever the bootstrap returned for this type — the shapes are
 * per-widget and deliberately not unified, because a goal's progress and a
 * feed's items have nothing in common beyond both being state.
 */
export function WidgetRender({
  widgetType,
  config,
  state,
  token = null,
}: {
  widgetType: string;
  config: Record<string, unknown> | null;
  state: Record<string, unknown>;
  /**
   * The browser source's token, for the emote image proxy.
   *
   * Absent in the gallery and the editor, where the page already has a session
   * and the proxy accepts that instead.
   */
  token?: string | null;
}): React.JSX.Element {
  const { vars, className } = styleOf(config);

  return (
    <div className={className} style={vars}>
      <div className="widget__surface">
        {widgetType.endsWith('GOAL') ? (
          <GoalBody state={state} />
        ) : widgetType === 'ACTIVITY_FEED' ? (
          <ul className="widget__feed">
            {((state.items as FeedItem[] | undefined) ?? []).map((item, index) => (
              <FeedRow key={`${item.occurredAt}-${String(index)}`} item={item} />
            ))}
          </ul>
        ) : widgetType === 'CHAT_OVERLAY' ? (
          <ChatBody
            config={config}
            messages={(state.messages as ChatLine[] | undefined) ?? []}
            emotes={(state.emotes as EmoteMap | undefined) ?? {}}
            token={token}
          />
        ) : widgetType === 'EVENT_LABELS' ? (
          <EventLabel config={config} latest={state.latest as FeedItem | null} />
        ) : null}
      </div>
    </div>
  );
}

export type { FeedItem, ChatLine };
